import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { BaseSessionService } from '../services/base-session.service';
import { SESSION_EXPIRED_MESSAGE, TokenRefreshService, isSessionEnded } from '../services/token-refresh.service';

/**
 * Endpoints that name their show in the path (`/v1/{show}/…`) resolve the org
 * from that segment. The rest of the API is still flat (`/v1/entries`,
 * `/v1/orders`, …) and has no other way to learn the org, so those keep the
 * header. Sending it on a show-scoped URL is what broke GetFamilyMembers: the
 * session said `sa` while the path said `tsp`.
 */
const SHOW_SCOPED_URL = /\/v1\/(sa|fourh|hdgs|tsp)\//i;

const LOGIN_URL = /\/(Login|login|auth)\/|\/Master\/GetEnvironmentConfigs/;

const isLoginEndpoint = (url: string | null | undefined): boolean =>
  !!url && LOGIN_URL.test(url);

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private session: BaseSessionService,
    private tokenRefresh: TokenRefreshService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Login endpoints (the refresh call included) never trigger a refresh.
    if (isLoginEndpoint(request.url)) {
      return this.send(request, next);
    }

    if (this.tokenRefresh.shouldRefreshToken()) {
      return this.refresh().pipe(
        switchMap((token) => this.sendWithRetry(request, next, token))
      );
    }

    return this.sendWithRetry(request, next, this.currentToken());
  }

  private send(request: HttpRequest<any>, next: HttpHandler, token?: string): Observable<HttpEvent<any>> {
    return next
      .handle(this.addAuthHeaders(request, token))
      .pipe(catchError((err) => this.handleError(err)));
  }

  /** The server can reject a token whose `exp` still looks live here (idle
   *  timeout, clock skew), so a 401 earns one refresh and retry. */
  private sendWithRetry(request: HttpRequest<any>, next: HttpHandler, token?: string): Observable<HttpEvent<any>> {
    return next.handle(this.addAuthHeaders(request, token)).pipe(
      catchError((err) => {
        if (err?.status !== 401) {
          return this.handleError(err);
        }
        // A parallel request may have refreshed already — reuse its token
        // rather than spend another of the server's MaxRefreshCount.
        const current = this.currentToken();
        const retryToken$ = current && current !== token ? of(current) : this.refresh();
        return retryToken$.pipe(switchMap((fresh) => this.send(request, next, fresh)));
      })
    );
  }

  /** Only a refresh that says the session is over signs the user out — not an
   *  ordinary error from the request it guarded, nor a refresh that never reached
   *  the server or failed for a server-side reason. */
  private refresh(): Observable<string> {
    return this.tokenRefresh.refreshToken().pipe(
      catchError((err) => (isSessionEnded(err) ? this.expireSession() : throwError(() => err)))
    );
  }

  private currentToken(): string | undefined {
    return this.session.getSessionItem(BaseSessionService.KEY_AUTH_TOKEN);
  }

  /** Callers get the session-expired text, never the raw refresh/401 error. */
  private expireSession(): Observable<never> {
    this.tokenRefresh.expireSession();
    return throwError(() => new Error(SESSION_EXPIRED_MESSAGE));
  }

  private addAuthHeaders(req: HttpRequest<any>, token?: string): HttpRequest<any> {
    const authToken = token || this.currentToken();
    const headers: Record<string, string> = {};
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    // Skipped where the URL already carries the show, and never applied over a
    // header the caller set explicitly.
    const abbreviation = this.session.getSessionItem(BaseSessionService.ORG_ABBREVIATION);
    if (
      abbreviation &&
      !SHOW_SCOPED_URL.test(req.url) &&
      !req.headers.has('X-Abbreviation')
    ) {
      headers['X-Abbreviation'] = abbreviation;
    }

    return req.clone({ setHeaders: headers });
  }

  private handleError(err: HttpErrorResponse): Observable<never> {
    if (err.status === 401 && !isLoginEndpoint(err.url)) {
      return this.expireSession();
    } else if (err.status === 0) {
      err = new HttpErrorResponse({
        error: {
          message:
            'The server is currently unreachable. Please check your internet connection and try again.'
        },
        status: 0,
        statusText: 'Network Error',
        url: err.url || undefined
      });
    }

    return throwError(() => err);
  }
}
